import * as React from "react";
import styled from "styled-components";
import {inject, observer} from "mobx-react";
import type {AppState} from "../../../../data/state/AppState";
import type {TransportLayer} from "../../../../data/TransportLayer";
import type {OrganizationDto, PointDouble, UpdateOrganizationImageRequest} from "../../../../generated/api/base";
import {ImageType} from "../../../../generated/api/base";
import {ImageUtils} from "../../../../utils/image/ImageUtils";
import {ButtonStyled, ButtonV5} from "../../button/ButtonV5";
import {FieldV5} from "../../details/FieldV5";
import {IconEditorStyled, IconEditorV5} from "../../abstract/view/catalog/IconEditorV5";
import {FileDropperReactV5, FileDropperStyled} from "../../interaction/FileDropperReactV5";
import {ImageUploadPreprocessor} from "../../../../utils/image/ImageUploadPreprocessor";
import {FileUtils} from "../../../../utils/file/FileUtils";
import {XHRLoader} from "../../../../utils/loader/XHRLoader";
import {Constants} from "../../../modules/space/spaceeditor/logic3d/Constants";
import {PopupV5} from "../../popup/PopupV5";
import {colorPalette} from "../../styles/colorPalette";
import {ClickToEditInputV5} from "../../input/clicktoedit/ClickToEditInputV5";
import {baseDistance, fontSize, radius, VerticalFlexStyle} from "../../styles/styles";
import {CreatePopupFieldStyled} from "../../popup/CreatePopupField.styled";
import {FieldStyled} from "../../details/Field.styled";
import {SingleLineLabelStyled} from "../../details/datatypes/SingleLineLabelV5";
import {notify} from "../../../../utils/Notify";
import {NotificationType} from "../../../notification/Notification";

interface IOrganizationSettingsProps {
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface IOrganizationSettingsStates {
	newImage: string;
	existingImage: string;
	imageAspectRatio: number;
	iconTranslate: PointDouble;
	iconOrientation: number;
	scale: number;
	isPopupOpen: boolean;
	isSaving: boolean;
	isSyncing: boolean;
}

@inject("appState")
@inject("transport")
@observer
export class OrganizationSettingsV5 extends React.Component<IOrganizationSettingsProps, IOrganizationSettingsStates> {
	private _svgRef = React.createRef<SVGSVGElement>();
	private _newImageType: ImageType;
	private _prevScale: number = 1;
	private parentRef = React.createRef<HTMLDivElement>();

	constructor(props: IOrganizationSettingsProps) {
		super(props);
		this.state = {
			newImage: null,
			existingImage: null,
			imageAspectRatio: 1,
			iconTranslate: {x: 0.5, y: 0.5},
			iconOrientation: 0,
			scale: 1,
			isPopupOpen: false,
			isSaving: false,
			isSyncing: false,
		};
	}

	private onFileInputChange = async (files: FileList) => {
		if (files.length > 0) {
			const file = files[0];

			const data = await ImageUploadPreprocessor.getImageDataForUpload(this.props.appState.fonts, file, false);

			this._newImageType = data.imageType;

			if (this._newImageType === ImageType.SVG) {
				data.fullImageData = await FileUtils.readAsText(file);
			}

			this.setState({
				newImage: data.fullImageData,
				imageAspectRatio: data.aspectRatio,
			});
		}
	};

	private onPopupOpen = () => {
		this.setState({
			isPopupOpen: true,
		});
	};

	private onPopupClose = () => {
		this.setState({
			isPopupOpen: false,
			newImage: null,
			scale: 1,
		});
	};

	private updateScale = (newScale: number) => {
		this.setState({scale: newScale});
	};

	private onSaveClick = async () => {
		this.setState({
			isSaving: true,
		});
		const {newImage, scale} = this.state;
		const isScaleChanged = this._prevScale !== scale;

		if (newImage || isScaleChanged) {
			const isSVG = this._newImageType === ImageType.SVG;
			const generatedImage = await ImageUploadPreprocessor.createCompressedImageFromSVG(
				this.props.appState.fonts,
				this._svgRef.current,
				isSVG,
				true,
				Constants.RESOLUTION.LOGO,
			);
			const params: UpdateOrganizationImageRequest = {
				image: generatedImage,
				imageType: isSVG ? ImageType.SVG : ImageType.PNG,
			};
			const {result, error} = await this.props.transport.requestForOrganization<OrganizationDto>({
				url: "organizations/uploadlogo",
				method: XHRLoader.METHOD_POST,
				params,
			});

			if (error) {
				console.error(error);
			} else {
				this.props.appState.actions.getCurrentOrganization().applyData(result);
				await this.loadLogo();
			}

			this._prevScale = scale;
			this.onPopupClose();
		} else {
			console.log(newImage, "No file selected, or something went wrong");
		}
		this.setState({
			isSaving: false,
		});
	};

	private async loadLogo() {
		const {transport} = this.props;
		let imageAspectRatio = 1;

		const orgLogoURL = transport.getCurrentOrganizationLogo();
		const img = orgLogoURL && (await ImageUtils.loadImage(orgLogoURL));
		const innerPart = img && (await ImageUtils.image2RasterBase64String(this.props.appState.fonts, img));

		this.setState({
			newImage: null,
			existingImage: innerPart,
			imageAspectRatio,
		});
	}

	public handleSync = async () => {
		this.setState({
			isSyncing: true,
		});

		try {
			const {transport} = this.props;
			const {data, error} = await transport.services.auth.handShake();

			if (error) {
				throw new Error("Failed to initiate syncing. Please try again later.");
			}

			const token = data.authenticationCode;
			if (!token) {
				throw new Error("Invalid token. Please try again later.");
			}

			const url = `${transport.config.api.acc}/signin?xyiconApiKey=${token}`;
			window.open(url);
		} catch (error) {
			console.error(error);
			notify(this.props.appState.app.notificationContainer, {
				title: "Syncing failed",
				description: error.message ?? "Failed to sync accounts. Please try again later.",
				type: NotificationType.Warning,
			});
		} finally {
			this.setState({
				isSyncing: false,
			});
		}
	};

	public override componentDidMount() {
		this.loadLogo();
	}

	public override render() {
		const {appState} = this.props;
		const {newImage, scale, existingImage, isPopupOpen, imageAspectRatio, iconOrientation, iconTranslate} = this.state;
		const orgName = appState.actions.getCurrentOrganizationName();
		const accIntegrationSettings = appState.actions.getCurrentOrganizationSettings().accIntegrationSettings;
		const isUserAdmin = !!appState.user?.isAdmin;
		const isAccSyncEnabled = accIntegrationSettings?.isEnabled;

		return (
			<OrganizationSettingsStyled>
				<div className="OrganizationSettings">
					<div className="settingsContainer vbox">
						<div className="logoContainer">
							<div className="logo">
								{existingImage ? (
									<img
										src={existingImage}
										alt="Organization logo"
									/>
								) : (
									<div className="noLogo" />
								)}
							</div>
							{isUserAdmin && (
								<ButtonV5
									label={`${existingImage ? "Edit Logo" : "Upload Logo"}`}
									onClick={this.onPopupOpen}
									ref={this.parentRef}
								/>
							)}
						</div>
						<div className="organizationname">
							<FieldV5 label="Organization Name">
								<ClickToEditInputV5
									value={orgName}
									disabled={true}
									className="disabledInputField"
								/>
							</FieldV5>
						</div>
						{isAccSyncEnabled && (
							<div className="syncField">
								<FieldV5 label="Xyicon Forma">
									<ButtonV5
										label={this.state.isSyncing ? "Loading..." : "Open Portal"}
										type="secondary"
										disabled={this.state.isSyncing}
										onClick={this.handleSync}
									/>
								</FieldV5>
							</div>
						)}
					</div>
				</div>
				{isUserAdmin && isPopupOpen && (
					<PopupV5
						onClose={this.onPopupClose}
						label="Upload Logo"
						parentRef={this.parentRef.current}
						className="OrganizationCreation"
						width="400px"
						centerOnScreen={true}
						freezeRoot={true}
						closeOnClickOutside={false}
					>
						<CreatePopupFieldStyled>
							<OrganizationLogoInputStyled>
								{(newImage ?? existingImage) && (
									<IconEditorV5
										key={newImage ?? existingImage}
										selectedLayer="Image"
										replaceColor={false}
										isSVG={this._newImageType === ImageType.SVG}
										innerPart={newImage || existingImage}
										imageAspectRatio={imageAspectRatio}
										iconTranslate={iconTranslate}
										iconOrientation={iconOrientation}
										scale={scale}
										onIconTranslateChange={(newTranslate: PointDouble) => this.setState({iconTranslate: newTranslate})}
										svgRef={this._svgRef}
										sliderWidth={150}
										onScaleChange={this.updateScale}
									/>
								)}
								<FileDropperReactV5
									onFileInputChange={this.onFileInputChange}
									purpose="Drag and drop or click here to add new logo"
									accept="image/*"
									multiple={false}
								/>
								<ButtonV5
									label={this.state.isSaving ? "Saving..." : "Save"}
									onClick={this.onSaveClick}
									disabled={this.state.isSaving || (!newImage && this._prevScale === scale)}
								/>
							</OrganizationLogoInputStyled>
						</CreatePopupFieldStyled>
					</PopupV5>
				)}
			</OrganizationSettingsStyled>
		);
	}
}

const OrganizationSettingsStyled = styled.div`
	.OrganizationSettings {
		position: relative;
		height: 100%;

		.settingsContainer {
			padding: 40px;
			z-index: 0;
			width: 400px;

			.organizationname {
				padding-top: auto;
				padding-bottom: auto;

				${SingleLineLabelStyled} {
					cursor: default;
				}
			}

			${FieldStyled} {
				margin-top: ${baseDistance.lg};

				.label {
					min-width: 130px;
				}

				.element {
					padding-left: ${baseDistance.oh};

					.disabledInputField {
						width: 300px;
						color: ${colorPalette.gray.c950};
					}
				}
			}

			.logoContainer {
				align-items: center;
				display: flex;

				.logo {
					width: 100px;
					height: 100px;
					margin-right: 64px;
					flex-shrink: 0;
					border-radius: ${radius.sm};
					border: solid 1px ${colorPalette.gray.c300};

					img {
						width: 100%;
						height: 100%;
					}

					> .noLogo {
						width: 100%;
						height: 100%;
						border: 1px dashed ${colorPalette.primary.c500Primary};
					}
				}

				.btnContainer {
					position: relative;

					> span {
						color: ${colorPalette.primary.c500Primary};
						font-size: ${fontSize.md};
						position: absolute;
						left: 0;
						top: 14px;
					}

					input[type="file"] {
						opacity: 0;
						width: 200px;
					}
				}
			}
		}
		.syncField {
			${ButtonStyled} {
				width: 100px;
				.label {
					min-width: 100px;
					width: 100%;
					height: 15px;
					text-align: center;
					display: inline-block;
				}
			}
		}
	}
`;

const OrganizationLogoInputStyled = styled.div`
	${VerticalFlexStyle};
	min-height: 300px;
	align-items: center;
	gap: ${baseDistance.md};
	flex: 1;

	${FileDropperStyled} {
		align-self: stretch;
		flex: 1;
	}

	${ButtonStyled} {
		align-self: flex-end;
	}

	${IconEditorStyled} {
		.IconEditor {
			width: 200px;
			height: 200px;
			position: relative;
			border: 1px solid ${colorPalette.gray.c300};
			border-radius: ${baseDistance.md};
			overflow: hidden;
		}
	}
`;
